import React, {useMemo, useState} from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Spinner,
} from '@chakra-ui/react';
import {useGetTestLists} from '../../../../hooks/api/testList';
import Select from 'react-select';
import {ObjectId} from 'bson';
import {Controller, useForm} from 'react-hook-form';
import {AgentLabel} from '../../../../shared/validation';
import {useGenerateSegmentationAgent} from '../../../../hooks/api/agents';
import {useTask} from '../../../../hooks/api/tasks';
import {isGenerateAgentState} from '../../../../shared/tasks/state';
import {useQueryClient} from '@tanstack/react-query';
import {MetadataQueryKey} from '../../../../context/MetadataContext';
import {captureMessage} from '@sentry/react';
import {MinSegmentationTestListSize} from '../../../../shared/agent';
import {WizardContent} from './WizardContent';

export const SegmentationWizard = ({
  onContinue,
}: {
  onContinue: (agentId: ObjectId) => void;
}) => {
  const [agentId, setAgentId] = useState<ObjectId | undefined>();
  const testLists = useGetTestLists({});
  const generateSegmentationAgent = useGenerateSegmentationAgent();
  const queryClient = useQueryClient();

  const testListOptions = useMemo(
    () =>
      (testLists.data ?? []).map((testList) => ({
        label: testList.label,
        value: testList._id,
        isDisabled:
          testList.status.type === 'processing' ||
          testList.status.numAccounts < MinSegmentationTestListSize,
      })),
    [testLists.data]
  );

  const {isTaskInProgress} = useTask(
    {type: 'generateSegmentationAgent'},
    {
      enabled: !agentId,
      onTaskComplete: (task) => {
        const state = task.detailedState;
        if (!isGenerateAgentState(state) || !state.agentId) {
          captureMessage('Unknown state for generateSegmentationAgent', {
            extra: {state},
            level: 'error',
          });
          return;
        }

        setAgentId(state.agentId);

        // re-fetch customer so we have access to new agent
        queryClient.invalidateQueries({
          queryKey: [MetadataQueryKey],
        });
      },
    }
  );

  const {
    control,
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<{
    agentLabel: string;
    domain: string;
    testList: {
      label: string;
      value: ObjectId;
    };
  }>();

  const onGenerateAgent = handleSubmit((data) => {
    const {agentLabel, domain, testList} = data;
    if (!testList || !agentLabel || !domain) {
      return;
    }

    generateSegmentationAgent.mutate({
      agentLabel,
      domain,
      testListId: testList.value,
    });
  });

  if (isTaskInProgress || generateSegmentationAgent.isPending) {
    return (
      <WizardContent
        title="Your agent is planning..."
        footer="This usually takes ~20-35 seconds"
      >
        <Spinner size="xl" color="kred.300" />
      </WizardContent>
    );
  }

  if (agentId) {
    return (
      <WizardContent
        title="Your agent is ready!"
        footer="Now you can adjust the details and test the results"
      >
        <Button
          colorScheme="kbuttonblue"
          size="lg"
          onClick={() => onContinue(agentId)}
        >
          Let&apos;s Go
        </Button>
      </WizardContent>
    );
  }

  return (
    <WizardContent title="Segmentation Analyst">
      <form onSubmit={onGenerateAgent}>
        <Flex direction="column" gap="10" maxW="600px" w="100%">
          <FormControl isInvalid={!!errors.agentLabel}>
            <FormLabel>Agent Name</FormLabel>
            <Input
              autoComplete="off"
              bgColor="white"
              {...register('agentLabel', {
                required: true,
                minLength: AgentLabel.minLength,
                maxLength: AgentLabel.maxLength,
                pattern: AgentLabel.pattern,
              })}
            />
            <FormHelperText>
              We recommend giving your agent a descriptive name based on the job
              you want it to do.
            </FormHelperText>
          </FormControl>

          <FormControl isInvalid={!!errors.testList}>
            <FormLabel>
              Select a customer list to discover your high-value segments
            </FormLabel>
            <Controller
              name={'testList'}
              control={control}
              rules={{required: true}}
              render={({field, fieldState: {error}}) => (
                <Select
                  {...field}
                  isLoading={testLists.isPending}
                  options={testListOptions}
                  styles={{
                    control: (base) => ({
                      ...base,
                      // Outline the component in red when there is an error
                      borderColor: error ? 'red' : base.borderColor,
                      boxShadow: error ? '0 0 0 1px red' : base.boxShadow,
                      '&:hover': {
                        borderColor: error ? 'red' : base.borderColor,
                      },
                    }),
                  }}
                />
              )}
            />
            <FormHelperText>
              Minimum test list size is {MinSegmentationTestListSize}.
            </FormHelperText>
          </FormControl>

          <FormControl isInvalid={!!errors.domain}>
            <FormLabel>Enter your website URL for context</FormLabel>
            <Input
              autoComplete="off"
              bgColor="white"
              placeholder="e.g. example.com"
              {...register('domain', {
                required: true,
              })}
            />
          </FormControl>

          <Button
            colorScheme="kbuttonblue"
            onClick={(e) => {
              if (agentId) {
                onContinue(agentId);
                e.preventDefault();
              }
            }}
            type="submit"
            w="fit-content"
          >
            Generate Agent
          </Button>
        </Flex>
      </form>
    </WizardContent>
  );
};
